import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Swiper from "swiper/bundle";
import "swiper/css/bundle";
import { endpoint } from "../../api/endpoint";
import { apiHandler } from "../../api";

// Prefetch images eagerly to avoid lag on page load
function prefetchImages(urls) {
  urls.forEach((url) => {
    if (!url) return;
    const img = new Image();
    img.src = url;
  });
}

// Sort slides in descending order so the latest generation comes first (1st slide)
function sortSlidesDescending(slides) {
  if (!Array.isArray(slides) || slides.length <= 1) return slides || [];

  const slidesCopy = [...slides];

  const hasTimestamp = slidesCopy.some(
    (s) => s?.created_at || s?.createdAt || s?.timestamp || s?.date || s?.createdDate,
  );
  const hasNumericId = slidesCopy.some(
    (s) => s?.id !== undefined && !isNaN(Number(s.id)),
  );

  if (hasTimestamp) {
    slidesCopy.sort((a, b) => {
      const timeA = new Date(
        a?.created_at || a?.createdAt || a?.timestamp || a?.date || a?.createdDate || 0,
      ).getTime();
      const timeB = new Date(
        b?.created_at || b?.createdAt || b?.timestamp || b?.date || b?.createdDate || 0,
      ).getTime();
      return timeB - timeA;
    });
  } else if (hasNumericId) {
    slidesCopy.sort((a, b) => Number(b.id) - Number(a.id));
  } else {
    // If no explicit timestamp or numeric id exists, reverse array so latest (last element) comes first
    slidesCopy.reverse();
  }

  return slidesCopy;
}

function normalizeApiSections(payload, type, mode) {
  const sectionTitle = mode || (type === "on user" ? "On User" : "On Model");
  if (Array.isArray(payload?.products)) {
    if (payload.products.length === 0) return [];
    return [
      {
        id: "show-tell-results",
        title: sectionTitle,
        count: payload.products.length,
        slides: payload.products,
      },
    ];
  }
  if (Array.isArray(payload?.rows)) {
    if (payload.rows.length === 0) return [];
    return [
      {
        id: "show-tell-results",
        title: sectionTitle,
        count: payload.rows.length,
        slides: payload.rows,
      },
    ];
  }
  if (Array.isArray(payload)) return payload.filter((s) => !Array.isArray(s?.slides) || s.slides.length > 0);
  if (Array.isArray(payload?.modes)) return payload.modes.filter((s) => !Array.isArray(s?.slides) || s.slides.length > 0);
  if (Array.isArray(payload?.data)) return payload.data.filter((s) => !Array.isArray(s?.slides) || s.slides.length > 0);
  return [];
}

export const useVTOViewer = ({
  gender = "",
  type = "",
  mode = "",
  page = 1,
  limit = 10,
  search = "",
} = {}) => {
  const token = useSelector((state) => state.auth?.token);
  const userRole = useSelector((state) => state.auth?.role);
  const isUserRole = userRole === "user" || userRole !== "admin";
  const [debouncedSearch, setDebouncedSearch] = useState(search);
  const [activeView, setActiveView] = useState("mannequin");
  const [sliderPosition, setSliderPosition] = useState(50);
  const [activeSlideIndices, setActiveSlideIndices] = useState({
    women: 0,
    men: 0,
  });
  const [sectionsData, setSectionsData] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit,
    total: 0,
    totalPages: 1,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const onViewChange = (view) => {
    setActiveView(view);
  };

  const handleDragSlider = (e, containerElement) => {
    if (!containerElement) return;

    const rect = containerElement.getBoundingClientRect();
    const newPosition = Math.max(
      0,
      Math.min(100, ((e.clientX - rect.left) / rect.width) * 100),
    );
    setSliderPosition(newPosition);
  };

  const productSwiperRef = useRef(null);
  const modelSwiperRef = useRef(null);
  const productNextRef = useRef(null);
  const productPrevRef = useRef(null);
  const modelNextRef = useRef(null);
  const modelPrevRef = useRef(null);
  const productSwiperInstanceRef = useRef(null);
  const modelSwiperInstanceRef = useRef(null);
  const dragCleanupRef = useRef([]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => {
      clearTimeout(timerId);
    };
  }, [search]);

  // Fetch Show & Tell data from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const params = {};
        if (page > 1) {
          params.page = page;
          params.limit = limit;
        }
        if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
        if (gender && gender !== "all") params.gender = gender;
        if (type) params.type = type;
        if (mode) params.mode = mode;

        const response = await apiHandler({
          url: `${endpoint.API_BASE_URL}${endpoint.GET_SHOW_AND_TELL}`,
          method: "GET",
          params: Object.keys(params).length > 0 ? params : undefined,
          authToken: { token },
          noCache: true,
        });

        const payload = response?.data?.data ?? response?.data;
        const apiData = normalizeApiSections(payload, type, mode);

        const sectionsWithRefs = apiData.map((section) => {
          const rawSlides = Array.isArray(section?.slides) ? section.slides : [];
          const activeKey = gender === "women" ? "women" : "men";
          return {
            ...section,
            slides: rawSlides
              .map((item) => {
                let childSlides = Array.isArray(item?.slides) && item.slides.length > 0
                  ? sortSlidesDescending(
                      item.slides.map((s) => {
                        const activeFlag =
                          s?.bIsActive !== undefined
                            ? Boolean(s.bIsActive && s.bIsActive !== "0" && s.bIsActive !== 0)
                            : s?.isActive !== undefined
                              ? Boolean(s.isActive && s.isActive !== "0" && s.isActive !== 0)
                              : s?.status !== undefined
                                ? Boolean(s.status === true || s.status === 1 || s.status === "1" || s.status === "active")
                                : true;
                        return {
                          ...s,
                          id: s?.id ?? s?.iSlideId ?? s?.slideId ?? s?.iShowTellId,
                          bIsActive: activeFlag,
                          status: activeFlag,
                        };
                      }),
                    )
                  : item?.transformed || item?.preview
                    ? [
                        {
                          ...item,
                          id: item?.id ?? item?.iSlideId ?? item?.slideId ?? item?.iShowTellId,
                          bIsActive:
                            item?.bIsActive !== undefined
                              ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
                              : item?.isActive !== undefined
                                ? Boolean(item.isActive && item.isActive !== "0" && item.isActive !== 0)
                                : item?.status !== undefined
                                  ? Boolean(item.status === true || item.status === 1 || item.status === "1" || item.status === "active")
                                  : true,
                          status:
                            item?.bIsActive !== undefined
                              ? Boolean(item.bIsActive && item.bIsActive !== "0" && item.bIsActive !== 0)
                              : item?.status !== undefined
                                ? item.status
                                : (item?.isActive ?? item?.is_active ?? true),
                          original: item.original,
                          transformed: item.transformed,
                          preview: item.preview || item.transformed,
                          originalAlt: item.originalAlt || item.productTitle || item.title,
                          transformedAlt: item.transformedAlt || item.productTitle || item.title,
                        },
                      ]
                    : [];

                // When user role is "user", filter out inactive slides (bIsActive === 0)
                if (isUserRole) {
                  childSlides = childSlides.filter((s) => s.bIsActive);
                }

                return {
                  ...item,
                  title: item?.productTitle || item?.productName || item?.title || "Product",
                  original: item?.original || childSlides[0]?.original || "",
                  slides: childSlides,
                };
              })
              .filter((item) => !isUserRole || (Array.isArray(item.slides) && item.slides.length > 0)),
            swiperRef: activeKey === "women" ? "productSwiperRef" : "modelSwiperRef",
            nextRef: activeKey === "women" ? "productNextRef" : "modelNextRef",
            prevRef: activeKey === "women" ? "productPrevRef" : "modelPrevRef",
            activeKey,
          };
        });

        const fallbackTotal = sectionsWithRefs.reduce(
          (sum, section) => sum + (section.slides?.length || 0),
          0,
        );

        setSectionsData(sectionsWithRefs);
        setPagination({
          page: Number(payload?.page) || page,
          limit: Number(payload?.limit) || limit,
          total: Number(payload?.total) || fallbackTotal,
          totalPages: Number(payload?.totalPages) || Math.max(1, Math.ceil(fallbackTotal / limit)),
        });
        setActiveSlideIndices({
          women: 0,
          men: 0,
        });
        setError(null);
      } catch (err) {
        console.error("Error fetching Show & Tell data:", err);
        setError(err.message || "Failed to fetch Show & Tell data");
        setSectionsData([]);
        setPagination({
          page,
          limit,
          total: 0,
          totalPages: 1,
        });
      } finally {
        setLoading(false);
      }
    };

    // Fetch whenever token or query params change
    if (token) {
      fetchData();
    }
  }, [token, gender, type, mode, page, limit, debouncedSearch]);

  // Prefetch all slide images on mount to eliminate initial page lag
  useEffect(() => {
    if (sectionsData && sectionsData.length > 0) {
      const allUrls = sectionsData.flatMap((section) =>
        section.slides.flatMap((item) => {
          const urls = [item?.original];
          if (Array.isArray(item?.slides)) {
            item.slides.forEach((s) => {
              if (s?.original) urls.push(s.original);
              if (s?.preview || s?.transformed) urls.push(s.preview || s.transformed);
            });
          } else if (item?.preview || item?.transformed) {
            urls.push(item.preview || item.transformed);
          }
          return urls.filter(Boolean);
        }),
      );
      prefetchImages(allUrls);
    }
  }, [sectionsData]);

  useEffect(() => {
    if (sectionsData.length === 0) return undefined;

    // Use a small timeout to ensure DOM is ready after render
    const timer = setTimeout(() => {
      let productSwiper;
      let modelSwiper;

      // Initialize product swiper (Women)
      if (productSwiperRef.current) {
        productSwiper = new Swiper(productSwiperRef.current, {
          slidesPerView: 1,
          loop: true,
          navigation: {
            nextEl: productNextRef.current,
            prevEl: productPrevRef.current,
          },
          on: {
            slideChange: (swiper) => {
              setActiveSlideIndices((prev) => ({
                ...prev,
                women: swiper.realIndex,
              }));
            },
          },
        });
        productSwiperInstanceRef.current = productSwiper;
      }

      // Initialize model swiper (Men)
      if (modelSwiperRef.current) {
        modelSwiper = new Swiper(modelSwiperRef.current, {
          slidesPerView: 1,
          loop: true,
          navigation: {
            nextEl: modelNextRef.current,
            prevEl: modelPrevRef.current,
          },
          allowTouchMove: false,
          on: {
            slideChange: (swiper) => {
              setActiveSlideIndices((prev) => ({
                ...prev,
                men: swiper.realIndex,
              }));
            },
          },
        });
        modelSwiperInstanceRef.current = modelSwiper;
      }

      // Initialize drag handle functionality
      initializeDragHandles();
    }, 100);

    return () => {
      clearTimeout(timer);
      if (productSwiperInstanceRef.current) {
        productSwiperInstanceRef.current.destroy(true, true);
        productSwiperInstanceRef.current = null;
      }
      if (modelSwiperInstanceRef.current) {
        modelSwiperInstanceRef.current.destroy(true, true);
        modelSwiperInstanceRef.current = null;
      }
      // Cleanup drag handlers
      dragCleanupRef.current.forEach((cleanup) => cleanup());
      dragCleanupRef.current = [];
    };
  }, [sectionsData]);

  const initializeDragHandles = () => {
    // Cleanup previous handlers
    dragCleanupRef.current.forEach((cleanup) => cleanup());
    dragCleanupRef.current = [];

    const cleanupFns = [];
    document.querySelectorAll(`[data-drag-box]`).forEach((box) => {
      const handle = box.querySelector(`[data-drag-handle]`);
      const transformed = box.querySelector(`[data-transformed]`);

      if (!handle || !transformed) return;

      let dragging = false;

      const handlePointerDown = (e) => {
        dragging = true;
        e.preventDefault();
        e.stopPropagation();
      };

      // Start dragging
      handle.addEventListener("pointerdown", handlePointerDown);

      // Stop dragging
      const handlePointerUp = () => {
        dragging = false;
      };
      window.addEventListener("pointerup", handlePointerUp);

      // Drag movement
      const handlePointerMove = (e) => {
        if (!dragging) return;
        const rect = box.getBoundingClientRect();
        let x = e.clientX - rect.left;
        x = Math.max(0, Math.min(rect.width, x));

        handle.style.left = x + "px";
        transformed.style.clipPath = `inset(0 0 0 ${(x / rect.width) * 100}%)`;
      };
      window.addEventListener("pointermove", handlePointerMove);

      // Cleanup on resize
      const handleResize = () => {
        handle.style.left = "50%";
        handle.style.top = "";
        transformed.style.clipPath = "inset(0 0 0 50%)";
      };
      window.addEventListener("resize", handleResize);

      cleanupFns.push(() => {
        handle.removeEventListener("pointerdown", handlePointerDown);
        window.removeEventListener("pointerup", handlePointerUp);
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("resize", handleResize);
      });
    });
    dragCleanupRef.current = cleanupFns;
    return cleanupFns;
  };

  const refMap = {
    productSwiperRef,
    modelSwiperRef,
    productNextRef,
    productPrevRef,
    modelNextRef,
    modelPrevRef,
  };

  const slideTo = (activeKey, index) => {
    const swiper = activeKey === "women" ? productSwiperInstanceRef.current : modelSwiperInstanceRef.current;
    if (swiper) {
      swiper.slideToLoop(index);
    }
  };

  return {
    searchTerm: search,
    activeView,
    onViewChange,
    sliderPosition,
    setSliderPosition,
    handleDragSlider,
    refMap,
    sectionsData,
    loading,
    error,
    pagination,
    initializeDragHandles,
    activeSlideIndices,
    slideTo,
  };
};
